import {Controller, FieldError, Path, RegisterOptions, useFormContext, FieldValues} from "react-hook-form";
import {SimpleDropdown} from "common";
import {ModalTooltip} from "common/component/ModalTooltip";
import React from "react";

type ModalSelectionInputProps<T extends FieldValues> = {
    options: Map<string, string>;
    label: string | React.ReactNode;
    disabled: boolean;
    required?: boolean;
    field: keyof T;
    dependentFields?: Array<keyof T> | undefined;
    optionalRules?: RegisterOptions;
    onChangeCallback?: () => void;
    placeholder?: string;
    labelPosition?: 'left' | 'top';
    tooltipContent?: string;
    errorStyle?: 'default' | 'warning';
    labelClassName?: string;
};

export function ModalSelectionInput<T extends FieldValues>({
                                           label,
                                           disabled,
                                           field,
                                           optionalRules,
                                           options,
                                           required = true,
                                           onChangeCallback,
                                           dependentFields,
                                           placeholder,
                                           labelPosition = 'left',
                                           tooltipContent,
                                           errorStyle = 'default',
                                           labelClassName,
                                       }: ModalSelectionInputProps<T>) {
    const {
        control,
        formState,
        setValue,
        clearErrors,
    } = useFormContext<T>();
    const errors = formState.errors as Record<keyof T, FieldError>;
    
    let rules: RegisterOptions = {required: {value: required, message: "Required"}};
    if (optionalRules) {
        rules = optionalRules;
    }

    const isLabelTop = labelPosition === 'top';

    const renderDropdown = () => (
        <Controller
            name={field as string as Path<T>}
            control={control as any}
            rules={rules}
            render={
                ({field: {value}}) =>
                    <SimpleDropdown
                        options={options}
                        selected={value}
                        disabled={disabled}
                        hasErrorBorder={errors[field] !== undefined}
                        placeholder={placeholder || '-- Select --'}
                        setSelected={(selected) => {
                            setValue(field as string as Path<T>, selected as any);
                            clearErrors(field as string as Path<T>);
                            if (onChangeCallback) {
                                onChangeCallback();
                            }
                        }}
                        textColor={"black-200"}
                    />
            }
        />
    );

    const renderLabel = () => (
        <>
            {label}
            {tooltipContent && <ModalTooltip content={tooltipContent} />}
        </>
    );
    return (
        <>
            <div className="w-full text-black-200 pb-2 text-base items-center">
                {isLabelTop ? (
                    // Top label layout
                    <div className="w-full">
                        <div className={`mb-2 ${labelClassName ?? ""}`}>
                            {renderLabel()}
                        </div>
                        <div className="w-full">
                            {renderDropdown()}
                            {errors[field] && errorStyle === 'default' && (
                                <div className="text-red-700 mt-1">{errors[field].message}</div>
                            )}
                        </div>
                    </div>
                ) : (
                    // Left label layout
                    <div className="flex items-center">
                        <div className={`w-2/6 text-right ${labelClassName ?? ""}`}>
                            <div className="flex items-center justify-end">
                                {label}
                                {tooltipContent && <ModalTooltip content={tooltipContent} />}
                            </div>
                        </div>
                        <div className="w-2/6 ml-4 items-center">
                            {renderDropdown()}
                            {errors[field] && errorStyle === 'default' && (
                                <div className="text-red-700 mt-1">{errors[field].message}</div>
                            )}
                        </div>
                    </div>
                )}
            </div>
            {
                errors[field] && errorStyle === 'warning' &&
                <div className="flex w-full pt-2">
                    {!isLabelTop && <div className="w-2/6 flex-shrink-0"></div>}
                    <div className={`${!isLabelTop ? 'ml-4' : ''} flex-1 flex items-start p-3 rounded border text-sm text-gray-800`}
                        style={{backgroundColor: "#FDF6EC", borderColor: "#F2C98A"}}>
                        <div className="flex-shrink-0 flex items-center justify-center rounded-full mr-2"
                            style={{backgroundColor: "#D9822B", minWidth: 20, width: 20, height: 20, marginTop: 1}}>
                            <span style={{color: 'white', fontSize: 11, fontWeight: 'bold', lineHeight: 1}}>!</span>
                        </div>
                        <span className="leading-relaxed font-bold">{errors[field].message}</span>
                    </div>
                </div>
            }
        </>
    );
}
